import { getCurrentDayKey } from "./eventDetailHelpers";
import { pickImageUrl, resolvePublicAssetUrl } from "./clubDetailHelpers";

const DAY_NAMES = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

function toFiniteNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function pickBottleName(item) {
  return item?.name || item?.title || item?.bottleName || item?.productName || item?.label || "Bottle";
}

export function pickBottleImage(item) {
  const raw = item?.image || item?.imageUrl || item?.photo || item?.thumbnail || item?.icon || item?.picture || "";
  if (typeof raw === "string" && raw.trim()) return resolvePublicAssetUrl(raw.trim());
  return pickImageUrl(item || {});
}

export function bottleOptionId(item, idx) {
  return String(item?.bottleServiceId || item?._id || item?.id || `${idx}`);
}

export function readBottleServiceId(item) {
  return String(item?.bottleServiceId || item?._id || item?.id || "").trim();
}

export function toBottleEntries(options) {
  return (options || []).map((item, idx) => ({
    id: bottleOptionId(item, idx),
    item,
  }));
}

export function buildBottleCatalog(event) {
  const raw = event?.bottleService || event?.bottleServices || event?.bottleMenu || event?.menu || event?.bottles || {};
  const toList = (value) => (Array.isArray(value) ? value : []);
  const direct = {
    alcoholic: toList(raw?.alcoholic),
    nonAlcoholic: toList(raw?.nonAlcoholic),
  };
  if (direct.alcoholic.length || direct.nonAlcoholic.length) return direct;
  const list = Array.isArray(raw) ? raw : [];
  const alcoholic = [];
  const nonAlcoholic = [];
  list.forEach((item) => {
    const group = String(item?.category || item?.type || item?.group || item?.section || "").toLowerCase();
    if (group.includes("non")) nonAlcoholic.push(item);
    else alcoholic.push(item);
  });
  return { alcoholic, nonAlcoholic };
}

export function getDefaultReservationPrice(event) {
  const raw = event?.bottleService || event?.bottleServices || event?.bottleMenu || event?.menu || event?.bottles || null;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return 0;
  return (
    toFiniteNumber(
      raw?.reservationPrice ??
        raw?.reservation_price ??
        raw?.reservationAmount ??
        raw?.reservation_amount ??
        raw?.reservationFee
    ) ?? 0
  );
}

export function getBottleServiceEventId(event) {
  return String(
    event?.bottleServiceEventId ||
      event?.bottleServiceId ||
      event?.bottleService?.eventId ||
      event?.bottleService?._id ||
      ""
  ).trim();
}

export function isBoothBottleServiceEnabled(event) {
  if (String(event?.type || "").trim().toLowerCase() !== "booth") return false;
  const flag = event?.enableBottleService ?? event?.bottleService?.enabled;
  if (flag === false || flag === 0 || flag === "0") return false;
  const catalog = buildBottleCatalog(event);
  return catalog.alcoholic.length > 0 || catalog.nonAlcoholic.length > 0;
}

export function createEmptyBottleSelectionRow() {
  return { selectedId: "", quantity: 1 };
}

export function getUnusedBottleOptions(entries, rows) {
  const selected = new Set((rows || []).map((row) => String(row?.selectedId || "").trim()).filter(Boolean));
  return (entries || []).filter((entry) => !selected.has(entry.id));
}

export function getSelectableBottleOptions(entries, rows, rowIdx) {
  const selectedByOthers = new Set(
    (rows || [])
      .map((row, idx) => (idx === rowIdx ? "" : String(row?.selectedId || "").trim()))
      .filter(Boolean)
  );
  return (entries || []).filter((entry) => !selectedByOthers.has(entry.id));
}

function dayKeyFromYmd(ymd) {
  if (!ymd) return getCurrentDayKey();
  const [y, m, d] = String(ymd).split("-").map(Number);
  if (!y || !m || !d) return getCurrentDayKey();
  return DAY_NAMES[new Date(y, m - 1, d).getDay()];
}

function pickItemReservationFee(item, fallbackReservationPrice = 0) {
  const candidates = [
    item?.reservationPrice,
    item?.reservation_price,
    item?.reservationAmount,
    item?.reservation_amount,
    item?.reservationFee,
    item?.bookingPrice,
    fallbackReservationPrice,
  ];
  for (const value of candidates) {
    const n = toFiniteNumber(value);
    if (n !== null) return n;
  }
  return 0;
}

export function pickBottleDayPricing(item, dateYmd, fallbackReservationPrice = 0) {
  const dayKey = dayKeyFromYmd(dateYmd);
  const dayRow = item?.dayWise?.[dayKey];
  if (dayRow && typeof dayRow === "object") {
    return {
      bottlePrice: Number(dayRow.bottlePrice ?? 0),
      reservationFee: Number(
        dayRow.reservationFee ?? pickItemReservationFee(item, fallbackReservationPrice) ?? 0
      ),
    };
  }
  const candidates = [
    item?.bottlePrice,
    item?.price,
    item?.amount,
    item?.cost,
    item?.sellingPrice,
    item?.servicePrice,
    item?.bottle_amount,
  ];
  let bottlePrice = 0;
  for (const value of candidates) {
    const n = toFiniteNumber(value);
    if (n !== null) {
      bottlePrice = n;
      break;
    }
  }
  return {
    bottlePrice,
    reservationFee: pickItemReservationFee(item, fallbackReservationPrice),
  };
}

export function formatBottleCurrency(value) {
  const amount = toFiniteNumber(value);
  if (amount === null) return "N/A";
  return `$${amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2)}`;
}

export function toBottleOptionLabel(item, dateYmd, fallbackReservationPrice = 0) {
  const name = pickBottleName(item);
  const { bottlePrice } = pickBottleDayPricing(item, dateYmd, fallbackReservationPrice);
  return `${name} (Bottle: ${formatBottleCurrency(bottlePrice)})`;
}

export function buildBottleSelectionPayload(rows, entries) {
  const map = new Map((entries || []).map((entry) => [entry.id, entry.item]));
  return (rows || [])
    .map((row) => {
      const item = map.get(String(row?.selectedId || ""));
      if (!item) return null;
      return {
        image: pickBottleImage(item),
        name: pickBottleName(item),
        bottleServiceId: readBottleServiceId(item),
        quantity: Math.max(1, Number(row?.quantity || 1)),
        qunatity: String(Math.max(1, Number(row?.quantity || 1))),
      };
    })
    .filter(Boolean);
}

export function buildReviewBottlePayload(items) {
  return (items || []).map((item) => ({
    BottleServiceId: item?.bottleServiceId || item?.BottleServiceId || "",
    name: item?.name || "",
    quantity: Number(item?.quantity || item?.qunatity || 0),
    ...(item?.image ? { image: item.image } : {}),
  }));
}
